import type { TFunction } from 'i18next';
import {
  getInterpretationRuleFieldLabel,
  getOperatorLabel,
} from './interpretation-rule-field-options';
import { preprocessRuleText } from './rule-text-preprocess';
import type {
  InterpretationRule,
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
  RuleAction,
  RuleCondition,
  RuleConditionLogic,
} from './types';

const PREVIEW_PREFIX = 'organisation.extraction-rules.preview';

export function formatConditionJoiner(
  t: TFunction,
  logic?: RuleConditionLogic
): string {
  return logic === 'OR'
    ? t('organisation.extraction-rules.condition-joiner-or')
    : t('common.and');
}

function fieldLabel(
  field: InterpretationRuleField,
  t: TFunction,
  fieldOptions?: InterpretationRuleFieldOptions
): string {
  return getInterpretationRuleFieldLabel(t, field, fieldOptions);
}

function resolveDisplayValue(
  field: InterpretationRuleField | null | undefined,
  value: string | number | null | undefined,
  fieldOptions?: InterpretationRuleFieldOptions
): string {
  if (value == null || String(value).trim() === '') return '';
  const raw = String(value);
  if (!field || !fieldOptions) return raw;

  const optionsByField: Partial<
    Record<InterpretationRuleField, { label: string; value: string }[]>
  > = {
    accountCode: fieldOptions.accountOptions,
    taxName: fieldOptions.taxOptions,
    contactName: fieldOptions.contactNameOptions,
    country: fieldOptions.countryOptions,
    itemId: fieldOptions.itemOptions,
    sourceType: fieldOptions.sourceTypeOptions,
    trackingOption1Id: fieldOptions.trackingOption1Options,
    trackingOption2Id: fieldOptions.trackingOption2Options,
  };

  const options = field ? optionsByField[field] : undefined;
  const match = options?.find((option) => String(option.value) === raw);
  return match?.label ?? raw;
}

function formatCondition(
  condition: RuleCondition,
  t: TFunction,
  fieldOptions?: InterpretationRuleFieldOptions
): string {
  if (!condition.field || !condition.operator) {
    return t(`${PREVIEW_PREFIX}.incomplete-condition`);
  }
  const field = fieldLabel(condition.field, t, fieldOptions);
  const op = getOperatorLabel(t, condition.operator);
  if (condition.operator === 'IS_EMPTY' || condition.operator === 'IS_NOT_EMPTY') {
    if (condition.operator === 'IS_NOT_EMPTY') {
      return t(`${PREVIEW_PREFIX}.is-found`, { field });
    }
    return `${field} ${op}`;
  }
  const displayValue = resolveDisplayValue(
    condition.field,
    condition.value,
    fieldOptions
  );
  const value =
    displayValue !== '' ? `'${displayValue}'` : '';
  return `${field} ${op}${value ? ` ${value}` : ''}`;
}

function formatAction(
  action: RuleAction,
  t: TFunction,
  fieldOptions?: InterpretationRuleFieldOptions
): string {
  const field = action.field
    ? fieldLabel(action.field, t, fieldOptions)
    : t(`${PREVIEW_PREFIX}.field-placeholder`);
  const displayValue = resolveDisplayValue(
    action.field,
    action.value,
    fieldOptions
  );
  const value =
    displayValue !== ''
      ? `'${displayValue}'`
      : t(`${PREVIEW_PREFIX}.empty-placeholder`);
  return t(`${PREVIEW_PREFIX}.action-to`, { field, value });
}

export function formatRulePreview(
  rule: Pick<InterpretationRule, 'conditions' | 'actions' | 'conditionLogic'>,
  t: TFunction,
  fieldOptions?: InterpretationRuleFieldOptions
): string {
  const actions = rule.actions ?? [];
  if (actions.length === 0) {
    return '';
  }

  const formattedActions = actions.map((action) =>
    formatAction(action, t, fieldOptions)
  );
  const actionText =
    actions.length === 1
      ? t(`${PREVIEW_PREFIX}.update-single`, { action: formattedActions[0] })
      : t(`${PREVIEW_PREFIX}.update-multiple`, {
          actions: formattedActions.join(', '),
        });

  const conditions = rule.conditions ?? [];
  if (conditions.length === 0) {
    return t(`${PREVIEW_PREFIX}.always`, {
      actions: actionText.charAt(0).toLowerCase() + actionText.slice(1),
    });
  }

  const whenText = conditions
    .filter((condition) => condition.field && condition.operator)
    .map((condition) => formatCondition(condition, t, fieldOptions))
    .join(formatConditionJoiner(t, rule.conditionLogic));

  return t(`${PREVIEW_PREFIX}.when`, { actionText, whenText });
}

export function suggestRuleName(
  rule: Pick<InterpretationRule, 'conditions' | 'actions' | 'conditionLogic'>,
  t: TFunction
): string {
  const preview = formatRulePreview(rule, t);
  if (!preview) return t('organisation.extraction-rules.default-rule-name');
  return preview.length > 60 ? `${preview.slice(0, 57)}…` : preview;
}

function formatRuleNameFromDescription(description: string): string {
  const text = preprocessRuleText(description)
    .replace(/[.,;:!?]+$/g, '')
    .trim();
  if (!text) return '';

  const capped = text.length > 60 ? `${text.slice(0, 57)}…` : text;
  return capped.charAt(0).toUpperCase() + capped.slice(1);
}

export function isMeaningfulStructuredRuleName(name: string, t: TFunction): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;

  const defaultName = t('organisation.extraction-rules.default-rule-name');
  if (trimmed === defaultName) return false;

  const placeholders = [
    t(`${PREVIEW_PREFIX}.field-placeholder`),
    t(`${PREVIEW_PREFIX}.empty-placeholder`),
    t(`${PREVIEW_PREFIX}.incomplete-condition`),
  ];
  return !placeholders.some((placeholder) => trimmed.includes(placeholder));
}

/** Prefer a polished parsed preview; fall back to the user's description. */
export function suggestRuleNameFromDescription(
  description: string,
  rule: Pick<InterpretationRule, 'conditions' | 'actions' | 'conditionLogic'>,
  t: TFunction
): string {
  const structured = suggestRuleName(rule, t);
  if (isMeaningfulStructuredRuleName(structured, t)) {
    return structured;
  }

  const fromDescription = formatRuleNameFromDescription(description);
  if (fromDescription) return fromDescription;

  return structured || t('organisation.extraction-rules.default-rule-name');
}
